/**
 * Màn hình đầu tiên – Chọn vai trò (Demo/Mockup).
 * Bố cục gọn, ít khoảng trắng; responsive đẹp trên mobile và desktop.
 */
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  useTheme,
  useMediaQuery,
} from '@mui/material'
import { Person, Assignment, Gavel, AccountBalance, ArrowForward } from '@mui/icons-material'
import { useAppDispatch } from '../hooks/redux'
import { setDemoUser } from '../store/slices/authSlice'

const PRIMARY_COLOR = '#1A2B4A'

const ROLES: Array<{
  key: string
  label: string
  description: string
  icon: React.ReactNode
  path: string
  buttonLabel: string
}> = [
  {
    key: 'user',
    label: 'Đương sự',
    description: 'Theo dõi vụ án, nộp đơn khởi kiện và nhận thông báo tiến trình.',
    icon: <Person />,
    path: '/cases',
    buttonLabel: 'Bắt đầu với tư cách Đương sự',
  },
  {
    key: 'secretary',
    label: 'Thư ký',
    description: 'Tiếp nhận và xử lý sơ bộ đơn, quản lý hồ sơ, phối hợp Thẩm phán.',
    icon: <Assignment />,
    path: '/workspace',
    buttonLabel: 'Vào với vai trò Thư ký',
  },
  {
    key: 'judge',
    label: 'Thẩm phán',
    description: 'Điều hành phiên, xét xử vụ án, lịch làm việc và ra quyết định.',
    icon: <Gavel />,
    path: '/workspace',
    buttonLabel: 'Vào với vai trò Thẩm phán',
  },
  {
    key: 'chief_judge',
    label: 'Chánh án',
    description: 'Phân công án, báo cáo năng suất, cài đặt và tổng quan đơn vị.',
    icon: <AccountBalance />,
    path: '/workspace',
    buttonLabel: 'Vào với vai trò Chánh án',
  },
]

function RoleSelectPage() {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const handleSelect = (key: string, path: string) => {
    dispatch(setDemoUser(key))
    navigate(path, { replace: true })
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        background: 'linear-gradient(160deg, #f0f4f8 0%, #e2e8f0 40%, #e8ecf1 100%)',
        pb: { xs: 3, sm: 4 },
      }}
    >
      {/* Nội dung chính: canh từ trên xuống, không căn giữa để tránh dư trắng */}
      <Box
        sx={{
          width: '100%',
          maxWidth: 960,
          mx: 'auto',
          flex: 1,
          px: { xs: 2, sm: 3 },
          pt: { xs: 2.5, sm: 3, md: 4 },
        }}
      >
        <Box sx={{ textAlign: 'center', mb: { xs: 2.5, sm: 3 } }}>
          <Typography
            variant="overline"
            color="text.secondary"
            sx={{ letterSpacing: 1, fontSize: { xs: '0.65rem', sm: '0.7rem' } }}
          >
            Bước 1: Chọn vai trò
          </Typography>
          <Typography
            variant="h4"
            fontWeight={700}
            sx={{
              color: PRIMARY_COLOR,
              fontSize: { xs: '1.375rem', sm: '1.75rem', md: '1.875rem' },
              letterSpacing: '-0.02em',
              mt: 0.25,
              mb: 0.25,
            }}
          >
            UEL E-Court
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}>
            Vui lòng chọn vai trò
          </Typography>
        </Box>

        {/* Grid 1 cột mobile, 2 cột tablet+ */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
            gap: { xs: 1.5, sm: 2 },
            width: '100%',
          }}
        >
          {ROLES.map((r) => (
            <Card
              key={r.key}
              elevation={0}
              sx={{
                cursor: 'pointer',
                borderRadius: 2,
                border: '2px solid',
                borderColor: 'divider',
                overflow: 'hidden',
                transition: 'all 0.2s ease',
                minHeight: isMobile ? 0 : 180,
                '&:hover': {
                  boxShadow: '0 12px 32px rgba(26, 43, 74, 0.14)',
                  transform: 'translateY(-2px)',
                  borderColor: 'primary.main',
                  bgcolor: 'background.paper',
                },
                '&:active': { transform: 'scale(0.99)' },
              }}
              onClick={() => handleSelect(r.key, r.path)}
            >
              <CardContent
                sx={{
                  p: { xs: 2, sm: 2.25 },
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'stretch',
                  textAlign: 'left',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, flex: 1, minHeight: 0 }}>
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      flexShrink: 0,
                      borderRadius: 1.5,
                      bgcolor: PRIMARY_COLOR,
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      '& .MuiSvgIcon-root': { fontSize: 22 },
                    }}
                  >
                    {r.icon}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="subtitle1"
                      fontWeight={600}
                      sx={{
                        color: PRIMARY_COLOR,
                        fontSize: { xs: '0.9375rem', sm: '1rem' },
                        mb: 0.25,
                      }}
                    >
                      {r.label}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        fontSize: { xs: '0.8125rem', sm: '0.875rem' },
                        lineHeight: 1.4,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    >
                      {r.description}
                    </Typography>
                  </Box>
                </Box>
                <Button
                  variant="contained"
                  fullWidth
                  disableElevation
                  endIcon={<ArrowForward sx={{ fontSize: 18 }} />}
                  sx={{
                    mt: 1.5,
                    py: { xs: 1.25, sm: 1.125 },
                    minHeight: 44,
                    borderRadius: 1.5,
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: { xs: '0.875rem', sm: '0.9375rem' },
                    bgcolor: PRIMARY_COLOR,
                    '&:hover': { bgcolor: '#0f1e35' },
                  }}
                >
                  {r.buttonLabel}
                </Button>
              </CardContent>
            </Card>
          ))}
        </Box>
      </Box>
    </Box>
  )
}

export default RoleSelectPage
